<template>
<div class="o-help-element">
	<div
		class="o-help-element_trigger | fixed right-0 bottom-0 z-64 w-12 md:w-14 m-4 transition-all cursor-pointer text-black hover:text-neutral-700"
		:class="{ 'is-animated': animationPlaying, 'is-invisible': !tutorialName, 'is-navbar-visible': navbarDisplayed }"
		@animationend="animationPlaying = false;"
		@click="openDialog"
	>
		<svg viewBox="0 0 56 56" fill="none" xmlns="http://www.w3.org/2000/svg">
			<path d="m 51.628087,43.023526 c 3.7049,-5.826 5.1259,-12.8152 3.9909,-19.6254 -1.135,-6.8101 -4.7452,-12.9609 -10.1384,-17.2710998 -5.3933,-4.3102 -12.1888,-6.47590003 -19.0815,-6.08120003 -6.8927,0.3948 -13.3966,3.32210003 -18.2624999,8.21990003 -4.8659,4.8976998 -7.75080003,11.4204998 -8.10060003,18.3155998 -0.3498,6.8952 1.86020003,13.6764 6.20540003,19.0415 4.3452999,5.365 10.5194999,8.9355 17.3368999,10.0258 6.8173,1.0902 13.7972,-0.3766 19.5989,-4.1189 l 8.0003,1.3368 c 0.245,0.0409 0.4961,0.0227 0.7326,-0.053 0.2369,-0.0758 0.4519,-0.2069 0.6269,-0.3825 0.176,-0.1756 0.307,-0.3906 0.383,-0.6271 0.075,-0.2365 0.094,-0.4877 0.053,-0.7326 z" fill="currentColor"/>
			<path d="m 24.914187,24.914226 c 0.3159,-1.3615 1.556,-2.3055 2.9525,-2.2475 1.5629,-0.0866 2.9027,1.1044 3,2.6666 0,2.0052 -2.8667,2.6667 -2.8667,4 m 3e-4,4.0021 c -0.0439,0 -0.0861,0.0175 -0.1171,0.0485 -0.0311,0.0311 -0.0486,0.0732 -0.0486,0.1171 v 0.0021 c 6e-4,0.0329 0.011,0.0648 0.0297,0.0918 0.0187,0.0271 0.045,0.0479 0.0756,0.06 0.0305,0.0122 0.064,0.015 0.0962,0.0081 0.0321,-0.0069 0.0615,-0.0231 0.0845,-0.0466 0.0229,-0.0235 0.0384,-0.0533 0.0445,-0.0856 0.006,-0.0323 0.0024,-0.0657 -0.0104,-0.096 -0.0129,-0.0302 -0.0344,-0.056 -0.0619,-0.0741 -0.0275,-0.018 -0.0597,-0.0276 -0.0925,-0.0274 m 11.9997,-5.3333 c 0,6.6274 -5.3726,12 -12,12 -6.6274,0 -12,-5.3726 -12,-12 0,-6.6274 5.3726,-12 12,-12 6.6274,0 12,5.3726 12,12 z" stroke="#ffffff" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
		</svg>
	</div>

	<BaseDialog ref="tutorialDialog" v-model="displayTutorialDialog" :historyMode="false" :contentClass="'!max-w-xl'" @dismissed="closeDialog">
		<div class="flex flex-col gap-6">
			<img
				:src="dialogContents[tutorialName].image"
				class="w-full h-36 bg-info-50 border border-info-50 rounded-lg"
			>

			<div class="text-left">
				<div class="heading-sm mb-2" v-text="dialogContents[tutorialName].header"/>
				<div class="text-body-base text-neutral-600" v-text="dialogContents[tutorialName].body"/>
			</div>

			<OButton
				class="self-end"
				:label="dialogContents[tutorialName].button"
				size="lg"
				fullWidthUntil="sm"
				@click="closeDialog('button')"
			/>
		</div>
	</BaseDialog>
</div>
</template>

<script lang="ts" setup>
import { computed, ref, watch } from 'vue';

import ImageRfpList from '@customer/images/tutorial/rfpList.svg?url';
import ImageContactList from '@customer/images/tutorial/contactList.svg?url';
import ImagePayments from '@customer/images/tutorial/payments.svg?url';
import ImageProfile from '@customer/images/tutorial/profile.svg?url';
import ImageServices from '@customer/images/tutorial/services.svg?url';

import OButton from 'ofe-common/components/Button/OButton.vue';
import BaseDialog from '@globals/components/global/BaseDialog.vue';

import useAnalytics from '@globals/composables/analytics';

import { useCustomerOnboardingStore } from '@customer/stores/customerOnboarding';

const analytics = useAnalytics();
const customerOnboarding = useCustomerOnboardingStore();

const props = defineProps<{
	/** Obecny URL strony */
	pathname: string,
	/** Lista niewyświetlonych tutoriali */
	tutorials: string[],
	/** Czy mobilny pasek nawigacyjny jest widoczny? */
	navbarDisplayed: boolean,
}>();

/**
 * Czy animacja niewyświetlonego tutoriala jest odgrywana?
 */
const animationPlaying = ref(false);

/**
 * Zwraca nazwę obecnie wyświetlanego tutoriala w zależności od URL-a na którym się znajdujemy.
 */
const tutorialName = computed(() => {
	const { pathname } = props;
	if ((pathname === '/' || pathname.includes('/konto/zapytania')) && !pathname.includes('_')) {
		return 'rfpList';
	}
	if (pathname.includes('/konto/kontakty')) {
		return 'contactList';
	}
	if (pathname === '/konto/ustawienia') {
		return 'payments';
	}
	const profile = pathname === '/konto/profil' || pathname === '/konto/profil/informacje';
	if (profile) {
		return 'profile';
	}
	if (pathname === '/konto/uslugi-produkty') {
		return 'services';
	}
	return '';
});

/**
 * Czy pokazać okno dialogowe z samouczkiem?
 */
const displayTutorialDialog = ref(false);

/**
 * Odgrywa animację w przypadku przejścia na stronę, gdzie nie został jeszcze wyświetlony tutorial.
 */
watch(tutorialName, async () => {
	animationPlaying.value = props.tutorials.includes(tutorialName.value);
	displayTutorialDialog.value = false;
});


/**
 * Treść okien dialogowych
 */
const dialogContents = {
	rfpList: {
		image: ImageRfpList,
		header: 'Zapytania',
		body: 'Tu znajdziesz zapytania od Zlecających dopasowane do Twoich preferencji. Odblokuj dane kontaktowe, składaj oferty i zdobywaj zlecenia! Każdego dnia mogą pojawić się nowe zapytania, a każde z nich to szansa na kolejne zlecenie.',
		button: 'Zobacz zapytania',
	},
	contactList: {
		image: ImageContactList,
		header: 'Kontakty',
		body: 'Tu znajdziesz dane kontaktowe Zlecających, odblokowane po złożeniu oferty. Nie zwlekaj – skontaktuj się jak najszybciej przez telefon, e-mail lub czat i zwiększ swoje szanse na zdobycie zlecenia!',
		button: 'Zobacz kontakty',
	},
	payments: {
		image: ImagePayments,
		header: 'Płatności',
		body: 'Tu sprawdzisz saldo punktów i doładujesz konto, aby bez przeszkód składać oferty Zlecającym. W tym miejscu sprawdzisz także historię swoich płatności i pobierzesz faktury.',
		button: 'Zobacz płatności',
	},
	profile: {
		image: ImageProfile,
		header: 'Profil',
		body: 'Tu uzupełnisz i zaktualizujesz dane, aby Twój profil stał się profesjonalną wizytówką i wyróżniał Cię na tle konkurencji. Im więcej informacji podasz, tym większa szansa, że Zlecający wybiorą właśnie Ciebie!',
		button: 'Zobacz profil',
	},
	services: {
		image: ImageServices,
		header: 'Usługi',
		body: 'Tu dodasz nowe usługi i dopasujesz ustawienia do swoich potrzeb, aby otrzymywać tylko interesujące Cię zlecenia. Włącz także dodatkowe funkcje, które zwiększą Twoją widoczność i pozwolą dotrzeć do bardziej zdecydowanych klientów.',
		button: 'Zobacz usługi',
	},
	'': {
		image: ImageRfpList,
		header: '',
		body: '',
		button: 'OK',
	},
};

/**
 * Otwiera okno dialogowe z tutorialem i wysyła informacje analityczne.
 */
function openDialog() {
	const { value: name } = tutorialName;
	displayTutorialDialog.value = true;

	analytics.NoteUserActionByName(
		'DISPLAY',
		'TUTORIAL_MODAL',
		{ tutorial: name },
	);

	analytics.sendGAEvent('click_helpChat', { bookmark: name });
	analytics.sendGAEvent('view_tutorial', { bookmark: name });

	customerOnboarding.removeTutorial(name);
}

/**
 * Wysyła informacje analityczne przy zamknięciu okna.
 * W przypadku, kiedy źródłem był przycisk, musimy ręcznie zamknąć okno.
 */
function closeDialog(source: string) {
	const { value: name } = tutorialName;
	const button = source === 'button';

	analytics.NoteUserActionByName(
		button ? 'BTN_ACTION_CLICK' : 'CLOSE',
		'TUTORIAL_MODAL',
		{ tutorial: tutorialName.value },
	);

	analytics.sendGAEvent(
		button ? 'click_begin_tutorial' : 'click_exit_tutorial',
		{ bookmark: name },
	);

	if (button) {
		displayTutorialDialog.value = false;
	}
}
</script>

<style lang="scss">
@keyframes o-help-element-animation {
	33% { transform: scale(0.8); }
	66% { transform: scale(1.1); }
}
.o-help-element {
	&_trigger {
		filter: drop-shadow(2px 4px 6px rgb(0 0 0 / 25%));
		opacity: 100;
		&.is-animated {
			animation: o-help-element-animation 0.6s linear;
			animation-delay: 1.2s;
		}
		&.is-invisible {
			opacity: 0;
			pointer-events: none;
		}
		&.is-navbar-visible {
			bottom: 4.25rem;
		}
	}
}

/* Ukryj trigger, gdy strona Usług się ładuje. */
/* Ukryj trigger, gdy jest wyświetlony hamburger. */
body:has(.services-container > .loader.is-visible) .o-help-element .o-help-element_trigger,
body:has(.global-menu .dropdown-wrapper.is-visible) .o-help-element .o-help-element_trigger {
	opacity: 0;
	pointer-events: none;
}

/* Podnieś trigger, gdy jest wyświetlony tost. */
body:has(.toast-message) .o-help-element .o-help-element_trigger {
	margin-bottom: 6.5rem;
	&.is-navbar-visible {
		margin-bottom: 2rem;
	}
}

/* Podnieś trigger, na stronie Usług. */
body:has(.services-container) .o-help-element .o-help-element_trigger {
	&.is-navbar-visible {
		bottom: 8.5rem;
	}
}
</style>
